import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faFireFlameCurved, 
  faWifi, 
  faArrowRight, 
  faShieldHalved, 
  faPiggyBank, 
  faHeadset,
  faBolt,
  faChartLine,
  faStar,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';
import { contattiAzienda } from '../utils/data';

const Home: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-36 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-20 left-10 w-[500px] h-[500px] bg-secondary rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-[600px] h-[600px] bg-accent rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2 mb-12 lg:mb-0"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
                <FontAwesomeIcon icon={faBolt} className="text-secondary text-sm" />
                <span className="text-sm font-medium">Consulenza gratuita e senza impegno</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold mb-6 leading-[1.1] font-display">
                Confronta le offerte per{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-yellow-300">luce, gas e internet</span>
              </h1>
              <p className="text-lg md:text-xl mb-6 text-white/75 max-w-xl leading-relaxed">
                Ti aiutiamo a confrontare le migliori offerte sul mercato. 
                La consulenza è completamente gratuita e non comporta alcun obbligo di sottoscrizione.
              </p>
              <p className="text-sm text-white/50 mb-10 max-w-xl">
                Sei libero di valutare e scegliere autonomamente se aderire a un'offerta. 
                Nessun vincolo, nessun costo nascosto.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/offerte" className="btn btn-secondary text-base px-8">
                  Confronta le Offerte
                </Link>
                <a href={`tel:${contattiAzienda.telefonoPrincipale}`} className="btn btn-outline border-white/30 text-white hover:bg-white hover:text-primary backdrop-blur-sm">
                  <FontAwesomeIcon icon={faHeadset} className="mr-2" /> Consulenza Gratuita
                </a>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:w-1/2 lg:pl-12"
            >
              <div className="bg-white/[0.08] backdrop-blur-xl border border-white/15 p-8 md:p-10 rounded-3xl shadow-2xl">
                <h3 className="text-2xl font-bold mb-2 text-center font-display">Calcola il tuo risparmio</h3>
                <p className="text-white/60 text-sm text-center mb-8">Simulazione indicativa e non vincolante</p>
                <form className="space-y-5">
                  <div>
                    <label className="block mb-2 text-white/80 text-sm font-medium">Tipo di fornitura</label>
                    <select className="w-full p-3.5 rounded-xl text-light-text bg-white border-0 focus:ring-2 focus:ring-secondary outline-none">
                      <option value="luce">Luce</option>
                      <option value="gas">Gas</option>
                      <option value="internet">Internet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-2 text-white/80 text-sm font-medium">Consumo annuale (kWh/Smc)</label>
                    <input type="number" className="w-full p-3.5 rounded-xl bg-white text-light-text border-0 focus:ring-2 focus:ring-secondary outline-none" placeholder="Es. 2500" />
                  </div>
                  <div>
                    <label className="block mb-2 text-white/80 text-sm font-medium">Numero di persone</label>
                    <input type="number" className="w-full p-3.5 rounded-xl bg-white text-light-text border-0 focus:ring-2 focus:ring-secondary outline-none" placeholder="Es. 3" />
                  </div>
                  <button type="submit" className="w-full btn btn-secondary text-base py-4">
                    Confronta Offerte
                  </button>
                  <p className="text-white/40 text-xs text-center">
                    I risultati sono puramente indicativi. Nessun obbligo di acquisto.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative -mt-8 z-20 px-4">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-light-border/30 p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { value: '12.000+', label: 'Clienti hanno usato il servizio', icon: faStar },
              { value: '10+', label: 'Anni di esperienza', icon: faChartLine },
              { value: '40+', label: 'Fornitori confrontati', icon: faShieldHalved },
              { value: '25+', label: 'Consulenti dedicati', icon: faHeadset },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/8 text-primary mb-3">
                  <FontAwesomeIcon icon={stat.icon} className="text-sm" />
                </div>
                <div className="text-2xl md:text-3xl font-extrabold text-light-text font-display">{stat.value}</div>
                <p className="text-light-muted text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-28">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">Cosa facciamo</span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-light-text font-display">I Nostri Servizi di Consulenza</h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Offriamo consulenza gratuita per aiutarti a confrontare le offerte disponibili sul mercato
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: faLightbulb,
                title: 'Consulenza Luce',
                desc: 'Ti aiutiamo a confrontare le tariffe luce disponibili sul mercato, per trovare quella più adatta alle tue esigenze. Senza obblighi.',
                gradient: 'from-primary/12 to-primary/4',
                color: 'text-primary',
                border: 'hover:border-primary/30',
              },
              {
                icon: faFireFlameCurved,
                title: 'Consulenza Gas',
                desc: 'Confrontiamo per te le offerte gas di diversi fornitori. La scelta finale è sempre tua, senza pressioni.',
                gradient: 'from-secondary/12 to-secondary/4',
                color: 'text-secondary',
                border: 'hover:border-secondary/30',
              },
              {
                icon: faWifi,
                title: 'Consulenza Internet',
                desc: 'Ti presentiamo le soluzioni internet disponibili nella tua zona. Fibra, ADSL o wireless: decidi tu liberamente.',
                gradient: 'from-accent/12 to-accent/4',
                color: 'text-accent',
                border: 'hover:border-accent/30',
              },
            ].map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * (index + 1) }}
                className={`card group ${service.border} transition-all duration-300`}
              >
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} ${service.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <FontAwesomeIcon icon={service.icon} className="text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-light-text font-display">{service.title}</h3>
                  <p className="mb-6 text-light-muted text-sm leading-relaxed">{service.desc}</p>
                  <Link to="/offerte" className="inline-flex items-center text-primary font-semibold text-sm group-hover:text-secondary transition-colors">
                    Scopri di più <FontAwesomeIcon icon={faArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-28 bg-gradient-to-b from-light-background to-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">I nostri vantaggi</span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-light-text font-display">Perché Affidarti a Noi</h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Un servizio trasparente, gratuito e senza vincoli per aiutarti a fare la scelta giusta
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: faPiggyBank,
                title: 'Servizio 100% Gratuito',
                desc: 'La nostra consulenza è completamente gratuita. Confrontiamo le offerte per te senza alcun costo o commissione a tuo carico.',
                gradient: 'from-secondary/15 to-secondary/5',
                color: 'text-secondary',
              },
              {
                icon: faShieldHalved,
                title: 'Nessun Obbligo',
                desc: 'La sottoscrizione di qualsiasi contratto è a tua totale discrezione. Non sei obbligato ad aderire a nessuna offerta.',
                gradient: 'from-primary/15 to-primary/5',
                color: 'text-primary',
              },
              {
                icon: faHeadset,
                title: 'Consulenza Personalizzata',
                desc: 'Un consulente dedicato analizza le tue esigenze e ti presenta le opzioni più adatte, lasciandoti libero di scegliere.',
                gradient: 'from-accent/15 to-accent/5',
                color: 'text-accent',
              },
            ].map((item, index) => (
              <motion.div 
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * (index + 1) }}
                className="p-8 text-center rounded-2xl bg-white border border-light-border/40 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} ${item.color} mb-6`}>
                  <FontAwesomeIcon icon={item.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-3 font-display">{item.title}</h3>
                <p className="text-light-muted text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="bg-light-background rounded-2xl border border-light-border/50 p-8 md:p-10 flex flex-col md:flex-row items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faInfoCircle} className="text-primary text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-light-text mb-2 font-display">Trasparenza e libertà di scelta</h3>
              <p className="text-light-muted text-sm leading-relaxed">
                Rinova Group offre un servizio di <strong>consulenza gratuita e comparazione delle offerte</strong> energetiche 
                e di connettività. Non siamo un fornitore diretto di energia, gas o servizi internet, ma un intermediario 
                indipendente. La nostra consulenza non comporta alcun costo per te e <strong>non implica alcun obbligo 
                di sottoscrizione</strong>. L'eventuale stipula di un contratto con un fornitore è una <strong>decisione 
                libera e autonoma del cliente</strong>. Le offerte presentate hanno carattere informativo e possono variare; 
                per i dettagli contrattuali si rimanda alla documentazione ufficiale dei singoli fornitori.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-16 px-4"
      >
        <div className="container mx-auto">
          <CallToAction 
            title="Vuoi confrontare le offerte?" 
            subtitle="Parla con un nostro consulente per una consulenza gratuita e senza impegno. La scelta è sempre tua."
            variant="primary"
          />
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
